import { kebabCase } from 'lodash';
import { projectSection } from '../../utils/projectPath';
import { findProjectPage, phaseLabel } from './projectMenu';

/** One step of the breadcrumb; the last is the page the user is on, the others link back up */
export type TCrumb = { label: string; to?: string };

/** Pages below another, outside projects: the parent, and this page's name when it has no detail */
const NESTED_PAGES: { pattern: RegExp; parent: TCrumb; label: string }[] = [
  { pattern: /^\/performers\/[^/]+/, parent: { label: 'Performers', to: '/performers' }, label: 'Performer' },
  { pattern: /^\/marketplace\/[^/]+/, parent: { label: 'Marketplace', to: '/marketplace' }, label: 'Talent manager' },
  { pattern: /^\/storage\/?$/, parent: { label: 'Account', to: '/account' }, label: 'Storage' },
  { pattern: /^\/archived\/?$/, parent: { label: 'Projects', to: '/' }, label: 'Archived projects' },
];

/**
 * The breadcrumb for a page: {Project} › {Phase} › {Page} on project pages, {Parent} › {Page} on
 * pages below another. `detail` names the record a detail page shows (an asset, a performer) and
 * becomes the last step. Undefined for top-level pages, which the header's nav already names.
 */
export const breadcrumbsFor = (pathname: string, projectName?: string, detail?: string): TCrumb[] | undefined => {
  const section = projectSection(pathname);
  if (section !== undefined) {
    const found = findProjectPage(section);
    if (!projectName || !found) return undefined;
    const projectPath = `/project/${kebabCase(projectName)}`;
    const pagePath = found.page.section ? `${projectPath}/${found.page.section}` : projectPath;
    return [
      { label: projectName, to: projectPath },
      { label: phaseLabel(found.phase) },
      { label: found.page.label, to: detail ? pagePath : undefined },
      ...(detail ? [{ label: detail }] : []),
    ];
  }

  const nested = NESTED_PAGES.find(({ pattern }) => pattern.test(pathname));
  return nested && [nested.parent, { label: detail || nested.label }];
};
